import { useTheme } from '@material-ui/core';
import { Tooltip } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router';

import ActionDisplay from '@portal/components/ActionDisplay/ActionDisplay';
import AdvancedButton from '@portal/components/AdvancedButton/AdvancedButton';
import GenerateBilletAmount from '@portal/components/GenerateBilletAmount/GenerateBilletAmount';
import GenerateBilletConfirmation from '@portal/components/GenerateBilletConfirmation/GenerateBilletConfirmation';
import QRCodeDetails from '@portal/components/QRCodeDetails/QRCodeDetails';
import QRCodeGenerate from '@portal/components/QRCodeGenerate/QRCodeGenerate';
import RolesType from '@portal/enum/rolesType';
import useReduxState from '@portal/hooks/useReduxState';
import * as MessageService from '~/services/message';

import Barcode from '@portal/assets/svg/receive-icons/ic_barcode.svg?react';
import Bank from '@portal/assets/svg/receive-icons/ic_coin.svg?react';
import CopyIcon from '@portal/assets/svg/receive-icons/ic_copy.svg?react';
import Pix from '@portal/assets/svg/receive-icons/ic_pix.svg?react';

import {
  DetailsAction,
  PixActions,
  SettingsActions,
  TransactionsActions,
} from '@portal/actions';
import AdvancedAlert from '@portal/components/AdvancedAlert/AdvancedAlert';
import AdvancedCard from '@portal/components/AdvancedCard/AdvancedCard';
import AdvancedHeader from '@portal/components/AdvancedHeader/AdvancedHeader';
import AdvancedInput from '@portal/components/AdvancedInput/AdvancedInput';
import AdvancedPanelContentHeader from '@portal/components/AdvancedPanelContentHeader/AdvancedPanelContentHeader';
import AdvancedSteps from '@portal/components/AdvancedSteps/AdvancedSteps';
import MaintenanceModal from '@portal/components/MaintenanceModal/MaintenanceModal';
import ThemedText from '@portal/components/ThemedText/ThemedText';
import { availableSoon, bankData, pix, receive } from '@portal/config/app.json';
import { getRouteStackPath } from '@portal/config/routes';
import { EventType } from '@portal/enum/analytics';
import { PixStatus } from '@portal/enum/PixStatus';
import { useNavigation } from '@portal/hooks/useNavigation';
import { AdditionalBilletlInfo, DetailType } from '@portal/models/enumerators';
import { AdditionalInfoType } from '@portal/models/module';
import { sendEvent } from '@portal/services/firebase';
import { translate } from '@portal/services/i18n';
import {
  formatMoney,
  maskCpfOrCnpj,
  maskPhoneInput,
  unmaskField,
} from '@portal/services/masks';
import { hasAccess } from '@portal/utils/hasAccess';
import { validateEmail } from '@portal/utils/validate';
import { validateCNPJ, validateCPF, validatePhone } from 'validations-br';

type ReceiveType = 'billet' | 'qrCode' | 'bankData';
type BankDataType = 'account' | 'branch' | 'bank' | 'taxidentifier';

interface StepConfig {
  visible: boolean;
  value: string;
  label: string;
  children: React.ReactNode;
}

interface ActionConfig {
  icon: React.ReactNode;
  title: string;
  text: string;
  onClick: () => void;
  hidden: boolean;
  key: string;
}

const initialBilletInfoValues = {} as AdditionalInfoType;

const additionalInputStyles = {
  fontSize: '22px',
  backgroundColor: 'transparent',
  marginBottom: '15px',
} as const;

enum TabIndexEnum {
  OPTIONS = '1',
  AMOUNT = '2',
  DESCRIPTION = '3',
  QR_CODE_DETAILS = '4',
  BILLET_CONFIRMATION = '5',
}

const Receive = () => {
  const location = useLocation();
  const navigate = useNavigation();
  const dispatch = useDispatch();
  const { palette } = useTheme();

  const {
    user: { roles },
    pix: { pixKeys },
    details: { transaction, type, error, qrCodeContent },
    settings: { settings, remoteConfig },
    theme: { currentTheme },
  } = useReduxState();

  const [tabKey, setTabKey] = useState('1');
  const [amount, setAmount] = useState('0');
  const [rawAmount, setRawAmount] = useState(0);
  const [receiveType, setReceiveType] = useState<ReceiveType>();
  const [pixKey, setPixKey] = useState<null | string>(null);
  const [description, setDescription] = useState('');
  const [openMaintenanceModal, setOpenMaintenanceModal] = useState(false);
  const [additionalBilletInfo, setAdditionalBilletInfo] =
    useState<AdditionalInfoType>(initialBilletInfoValues);
  const [hoveredIndex, setHoveredIndex] = useState<string | null>(null);

  useEffect(() => {
    const canLoadPixKeys =
      pix.enabled &&
      hasAccess(roles, [RolesType.PIX_MANAGE_KEYS, RolesType.PIX_CONTROL_KEYS]);

    if (!pixKeys && canLoadPixKeys) {
      dispatch(PixActions.getAllPixKeys());
    }
  }, [dispatch, pixKeys, pix.enabled, roles]);

  useEffect(() => {
    if (location.state === 'pix-charge') {
      setReceiveType('qrCode');
      setTabKey(TabIndexEnum.AMOUNT);
    }
  }, [location.state]);

  useEffect(() => {
    return () => {
      dispatch(DetailsAction.unsetDetails());
    };
  }, [dispatch]);

  useEffect(() => {
    if (qrCodeContent) {
      setTabKey(TabIndexEnum.QR_CODE_DETAILS);
    }
  }, [qrCodeContent]);

  useEffect(() => {
    if (transaction && type === DetailType.NEW) {
      setTabKey('4');
    }

    if (transaction?.id && type !== DetailType.NEW) {
      MessageService.success('PAGES.HOME.PIX.TRANSFER.SUCCESS');
      navigate(`/inicio/detalhes/${transaction.id}`);
    }

    if (error) {
      MessageService.error(error.message, { originalError: error });
      dispatch(DetailsAction.setDetailsError(null));
    }
  }, [dispatch, error, history, transaction, type]);

  useEffect(() => {
    if (!settings) {
      dispatch(SettingsActions.getSettings());
    }
    if (!remoteConfig) {
      dispatch(SettingsActions.getRemoteConfig());
    }
  }, [dispatch, remoteConfig, settings]);

  const changeAdditionalInfo = useCallback(
    (
      key: keyof typeof initialBilletInfoValues,
      value: AdditionalInfoType[keyof AdditionalInfoType]
    ) => {
      switch (key) {
        case AdditionalBilletlInfo.TAXIDENTIFIER:
          if (unmaskField(value).length <= 14) {
            setAdditionalBilletInfo((prev) => ({
              ...prev,
              [key]: unmaskField(value),
            }));
          }
          break;
        case AdditionalBilletlInfo.CELLPHONE:
          if (unmaskField(value).length <= 11) {
            setAdditionalBilletInfo((prev) => ({
              ...prev,
              [key]: unmaskField(value),
            }));
          }
          break;

        default:
          setAdditionalBilletInfo((prev) => ({ ...prev, [key]: value }));
          break;
      }
    },
    []
  );

  const handleQRCodeValue = useCallback(
    (willSpecifyValue = true) => {
      const limits = settings?.limits?.pix;
      if (
        (rawAmount === 0 ||
          (limits?.max && rawAmount > limits.max) ||
          rawAmount < (limits?.min ?? 0)) &&
        willSpecifyValue
      ) {
        MessageService.error('INVALID_AMOUNT', { domain: 'validation' });
        return;
      }

      if (
        (!pixKeys || !pixKeys.some((key) => key.status === PixStatus.ACTIVE)) &&
        receiveType === 'qrCode'
      ) {
        return MessageService.error('CHARGE_NO_KEYS');
      }

      setTabKey(TabIndexEnum.DESCRIPTION);
    },
    [pixKeys, rawAmount, receiveType, settings?.limits?.pix]
  );

  const handleGenerateBillet = useCallback(() => {
    const { taxIdentifier, cellphone, email, name } = additionalBilletInfo;

    if (receive.requestCompleteBilletInfo) {
      if (
        !taxIdentifier ||
        !(validateCPF(taxIdentifier) || validateCNPJ(taxIdentifier))
      ) {
        return MessageService.error('INVALID_TAX_IDENTIFIER', {
          domain: 'validation',
        });
      }

      if (!cellphone || !validatePhone(cellphone)) {
        return MessageService.error(
          'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.CELLPHONE',
          { domain: 'validation' }
        );
      }

      if (!email || !validateEmail(email)) {
        return MessageService.error(
          'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.EMAIL_INVALID',
          { domain: 'validation' }
        );
      }

      if (!name || name.length === 0 || !name.split(' ')[1]) {
        return MessageService.error(
          'PAGES.AUTH.REGISTER.ONBOARD.PF.ERRORS.NAME',
          { domain: 'validation' }
        );
      }
    }

    const limits = settings?.limits?.cashInBillet;

    if (!rawAmount || rawAmount === 0 || rawAmount < (limits?.min ?? 0)) {
      return MessageService.error('BILLET_VALID_MIN_AMOUNT', {
        interpolation: {
          interpolationField: 'minBilletValue',
          interpolationValue: formatMoney(
            settings?.limits?.cashInBillet?.min || 0
          ),
        },
      });
    }

    if (limits?.max && rawAmount > limits.max) {
      return MessageService.error('BILLET_VALID_MAX_AMOUNT', {
        interpolation: {
          interpolationField: 'maxBilletValue',
          interpolationValue: formatMoney(
            settings?.limits?.cashInBillet?.max || 0
          ),
        },
      });
    }

    const requestData: models.BilletInfoRequest = {
      billetInfo: {
        bankCode: '341',
        instructions: '',
        amount: rawAmount,
      },
    };

    if (receive.requestCompleteBilletInfo) {
      requestData.billetInfo.beneficiary = {
        cellphone: `55${cellphone}`,
        email,
        taxIdentifier,
        name,
      };
    }

    dispatch(TransactionsActions.generateBillet(requestData));
  }, [
    additionalBilletInfo,
    dispatch,
    rawAmount,
    settings?.limits?.cashInBillet,
  ]);

  const handleDownload = useCallback(() => {
    if (transaction?.billetRechargeInfo) {
      window.open(transaction.billetRechargeInfo.billetUrl);
    }
  }, [transaction?.billetRechargeInfo]);

  const handleEmail = useCallback(() => {
    if (transaction?.billetRechargeInfo) {
      dispatch(
        TransactionsActions.sendBilletByEmail(
          transaction.billetRechargeInfo.billetUrl
        )
      );
    }
  }, [dispatch, transaction?.billetRechargeInfo]);

  const handleCreateQRCode = useCallback(() => {
    const firstActiveKey = pixKeys?.find(
      (key) => key.status === PixStatus.ACTIVE
    );

    if (!firstActiveKey) {
      return MessageService.error('CHARGE_NO_KEYS');
    }

    const requestData: models.QRCodeRequest = {
      transaction: {
        amount: rawAmount,
        pixInfo: {
          key: pixKey ?? firstActiveKey.key,
          description,
        },
      },
    };
    dispatch(TransactionsActions.generateQRCode(requestData));
    setTabKey(TabIndexEnum.QR_CODE_DETAILS);
  }, [description, dispatch, pixKey, pixKeys, rawAmount]);

  const handleCopyCode = useCallback(() => {
    if (!transaction?.billetRechargeInfo?.typeableLine) {
      return;
    }

    navigator.clipboard.writeText(transaction.billetRechargeInfo.typeableLine);
  }, [transaction?.billetRechargeInfo?.typeableLine]);

  const actions: ActionConfig[] = useMemo(
    () => [
      {
        key: 'billet',
        icon: <Barcode />,
        title: translate('PAGES.HOME.RECEIVE.BILLET.TITLE'),
        text: translate('PAGES.HOME.RECEIVE.BILLET.DESCRIPTION'),
        onClick: () => {
          if (
            remoteConfig?.disableReceiveBillet ||
            remoteConfig?.disableCashIn
          ) {
            setOpenMaintenanceModal(true);
            return;
          }
          setReceiveType('billet');
          setTabKey(TabIndexEnum.AMOUNT);
          sendEvent(EventType.BOTTOM_MODAL_RECHARGE_BILLET);
        },
        hidden: !receive.enabled || !receive.billet,
      },
      {
        key: 'qrCodePix',
        icon: <Pix />,
        title: translate('PAGES.HOME.RECEIVE.QR_CODE_PIX.TITLE'),
        text: translate('PAGES.HOME.RECEIVE.QR_CODE_PIX.DESCRIPTION'),
        onClick: () => {
          if (remoteConfig?.disableCashIn) {
            setOpenMaintenanceModal(true);
            return;
          }
          setReceiveType('qrCode');
          setTabKey(TabIndexEnum.AMOUNT);
          sendEvent(EventType.BOTTOM_MODAL_RECHARGE_PIX);
        },
        hidden: !pix.enabled || !receive.enableQRCodePix,
      },
      {
        key: 'bankData',
        icon: <Bank fill={palette.primary.main} />,
        title: translate('PAGES.HOME.RECEIVE.BANK_DATA_RECEIVE.TITLE'),
        text: translate('PAGES.HOME.RECEIVE.BANK_DATA_RECEIVE.TEXT'),
        onClick: () => {
          if (remoteConfig?.disableCashIn) {
            setOpenMaintenanceModal(true);
            return;
          }
          setReceiveType('bankData');
          setTabKey('2');
          sendEvent(EventType.BOTTOM_MODAL_RECHARGE_PIX);
        },
        hidden: !bankData.enabled,
      },
    ],
    [palette.primary.main, remoteConfig]
  );

  const handleCopyBankData = useCallback((type: BankDataType) => {
    let textToCopy = '';

    if (type === 'bank') {
      textToCopy = bankData?.bank ?? '';
    } else if (type === 'account') {
      textToCopy = bankData?.account ?? '';
    } else if (type === 'branch') {
      textToCopy = bankData?.branch ?? '';
    } else if (type === 'taxidentifier') {
      textToCopy = bankData?.taxIdentifier ?? '';
    }

    navigator.clipboard.writeText(textToCopy);
  }, []);

  const bankDataOptions = useMemo(
    () => [
      {
        title: translate('PAGES.HOME.RECEIVE.BANK_DATA.BANK'),
        text: bankData?.bank,
        onClick: () => handleCopyBankData('bank'),
        hidden: !bankData.bank,
        rightIcon: (
          <Tooltip
            title={translate('PAGES.HOME.RECEIVE.BANK_DATA.COPY')}
            trigger="click"
            placement="rightTop"
            mouseLeaveDelay={0.5}
            mouseEnterDelay={0.2}
          >
            <CopyIcon fill={palette.primary.main} fontSize={25} />
          </Tooltip>
        ),
      },
      {
        title: translate('PAGES.HOME.RECEIVE.BANK_DATA.AGENCY'),
        text: bankData?.branch,
        onClick: () => handleCopyBankData('branch'),
        hidden: !bankData.branch,
        rightIcon: (
          <Tooltip
            title={translate('PAGES.HOME.RECEIVE.BANK_DATA.COPY')}
            trigger="click"
            placement="rightTop"
            mouseLeaveDelay={0.5}
            mouseEnterDelay={0.2}
          >
            <CopyIcon fill={palette.primary.main} fontSize={25} />
          </Tooltip>
        ),
      },
      {
        title: translate('PAGES.HOME.RECEIVE.BANK_DATA.ACCOUNT'),
        text: bankData?.account,
        onClick: () => handleCopyBankData('account'),
        hidden: !bankData.account,
        rightIcon: (
          <Tooltip
            title={translate('PAGES.HOME.RECEIVE.BANK_DATA.COPY')}
            trigger="click"
            placement="rightTop"
            mouseLeaveDelay={0.5}
            mouseEnterDelay={0.2}
          >
            <CopyIcon fill={palette.primary.main} fontSize={25} />
          </Tooltip>
        ),
      },
      {
        title: translate('PAGES.HOME.RECEIVE.BANK_DATA.TAX_IDENTIFIER'),
        text: maskCpfOrCnpj(bankData?.taxIdentifier),
        onClick: () => handleCopyBankData('taxidentifier'),
        hidden: !bankData.taxIdentifier,
        rightIcon: (
          <Tooltip
            title={translate('PAGES.HOME.RECEIVE.BANK_DATA.COPY')}
            trigger="click"
            placement="rightTop"
            mouseLeaveDelay={0.5}
            mouseEnterDelay={0.2}
          >
            <CopyIcon fill={palette.primary.main} fontSize={25} />
          </Tooltip>
        ),
      },
    ],
    [
      bankData.account,
      bankData.bank,
      bankData.branch,
      bankData?.taxIdentifier,
      handleCopyBankData,
      palette.primary.main,
    ]
  );

  const renderBackButton = useCallback(
    () => (
      <div className="receive__back-button">
        <AdvancedButton
          text={translate('PAGES.HOME.RECEIVE.BUTTONS.GO_BACK')}
          onClick={() => {
            setTabKey((parseInt(tabKey, 10) - 1).toString());
            setAmount('0');
            setRawAmount(0);
          }}
          variant="outlined"
        />
      </div>
    ),
    [tabKey]
  );

  const stepOptions = useMemo(() => {
    const steps: StepConfig[] = [
      {
        visible: true,
        value: TabIndexEnum.OPTIONS,
        label: translate('PAGES.HOME.RECEIVE.TABS.ONE'),
        children: (
          <div className="receive__grid">
            {actions
              .filter((a) => !a.hidden)
              .map((action) => (
                <button
                  key={action.key}
                  className="receive__grid__button transparent-button"
                  onClick={action.onClick}
                  onMouseEnter={() => {
                    setHoveredIndex(action.key);
                  }}
                  onMouseLeave={() => setHoveredIndex(null)}
                  style={{
                    border: `2px solid ${currentTheme.borderColor}`,
                    background:
                      hoveredIndex === action.key
                        ? currentTheme.background
                        : 'transparent',
                  }}
                >
                  <div
                    className="receive__grid__button__icon"
                    style={{ background: currentTheme.background }}
                  >
                    {action.icon}
                  </div>
                  <div>
                    <ThemedText
                      variant="body"
                      className="receive__grid__button__title"
                    >
                      {action.title}
                    </ThemedText>
                    <ThemedText className="receive__grid__button__description">
                      {action.text}
                    </ThemedText>
                  </div>
                </button>
              ))}
          </div>
        ),
      },
      {
        visible: true,
        value: TabIndexEnum.AMOUNT,
        label: translate('PAGES.HOME.RECEIVE.TABS.TWO'),
        children: (
          <>
            {receiveType !== 'bankData' && (
              <>
                {receive.requestCompleteBilletInfo ? (
                  <>
                    <ThemedText
                      variant="body"
                      className="generate-billet-amount__text"
                    >
                      {translate(
                        'PAGES.HOME.BILLET_AND_PIX_VALUE.ADDITIONAL_INFO.TITLE'
                      )}
                    </ThemedText>
                    <ThemedText
                      variant="body"
                      className="generate-billet-amount__label"
                    >
                      {translate(
                        'PAGES.HOME.BILLET_AND_PIX_VALUE.ADDITIONAL_INFO.TAXID'
                      )}
                    </ThemedText>
                    <AdvancedInput
                      variant="standard"
                      hideVariantUnderline={true}
                      value={maskCpfOrCnpj(additionalBilletInfo.taxIdentifier)}
                      placeholder="xxx.xxx.xxx-xx"
                      autoFocus
                      onChange={(value) => {
                        changeAdditionalInfo(
                          AdditionalBilletlInfo.TAXIDENTIFIER,
                          value
                        );
                      }}
                      style={additionalInputStyles}
                    />
                    <ThemedText
                      variant="body"
                      className="generate-billet-amount__label"
                    >
                      {translate(
                        'PAGES.HOME.BILLET_AND_PIX_VALUE.ADDITIONAL_INFO.CELLPHONE'
                      )}
                    </ThemedText>
                    <AdvancedInput
                      variant="standard"
                      hideVariantUnderline={true}
                      value={maskPhoneInput(
                        additionalBilletInfo.cellphone || ''
                      )}
                      placeholder="(xx) x xxxx-xxxx"
                      onChange={(value) => {
                        changeAdditionalInfo(
                          AdditionalBilletlInfo.CELLPHONE,
                          value
                        );
                      }}
                      style={additionalInputStyles}
                    />
                    <ThemedText
                      variant="body"
                      className="generate-billet-amount__label"
                    >
                      {translate(
                        'PAGES.HOME.BILLET_AND_PIX_VALUE.ADDITIONAL_INFO.EMAIL'
                      )}
                    </ThemedText>
                    <AdvancedInput
                      variant="standard"
                      hideVariantUnderline={true}
                      value={additionalBilletInfo.email}
                      placeholder="email@email.com"
                      onChange={(value) => {
                        changeAdditionalInfo(
                          AdditionalBilletlInfo.EMAIL,
                          value
                        );
                      }}
                      style={additionalInputStyles}
                    />
                    <ThemedText
                      variant="body"
                      className="generate-billet-amount__label"
                    >
                      {translate(
                        'PAGES.HOME.BILLET_AND_PIX_VALUE.ADDITIONAL_INFO.NAME'
                      )}
                    </ThemedText>
                    <AdvancedInput
                      variant="standard"
                      hideVariantUnderline={true}
                      value={additionalBilletInfo.name}
                      placeholder="João Freitas"
                      onChange={(value) => {
                        changeAdditionalInfo(AdditionalBilletlInfo.NAME, value);
                      }}
                      style={additionalInputStyles}
                    />
                  </>
                ) : null}
                <GenerateBilletAmount
                  setBilletAmount={setAmount}
                  setBilletRawAmount={setRawAmount}
                  billetAmount={amount}
                  autoFocus={!receive.requestCompleteBilletInfo}
                  receiveType={receiveType}
                  onSubmit={(willSpecifyValue) => {
                    if (receiveType === 'billet') {
                      handleGenerateBillet();
                    } else {
                      handleQRCodeValue(willSpecifyValue);
                    }
                  }}
                />
                <div className="receive__buttons">
                  {renderBackButton()}
                  <AdvancedButton
                    text={translate(
                      `PAGES.HOME.RECEIVE.BUTTONS.${
                        receiveType === 'billet' ? 'CREATE_BILLET' : 'CONTINUE'
                      }`
                    )}
                    onClick={() =>
                      receiveType === 'billet'
                        ? handleGenerateBillet()
                        : handleQRCodeValue()
                    }
                  />
                </div>
              </>
            )}
            {receiveType === 'bankData' && (
              <AdvancedCard testId="bank-data-card">
                <ThemedText
                  variant="body"
                  className="generate-billet-amount__text"
                >
                  {translate('PAGES.HOME.RECEIVE.BANK_DATA.BANK_DATA_TITLE')}
                </ThemedText>
                <ThemedText
                  variant="body"
                  className="generate-billet-amount__label"
                >
                  {translate(
                    'PAGES.HOME.RECEIVE.BANK_DATA.BANK_DATA_DESCRIPTION'
                  )}
                </ThemedText>
                <ActionDisplay items={bankDataOptions} />
              </AdvancedCard>
            )}
          </>
        ),
      },
      {
        visible: receiveType === 'qrCode',
        value: TabIndexEnum.DESCRIPTION,
        label: translate('PAGES.HOME.RECEIVE.TABS.THREE.QR_CODE'),
        children: (
          <>
            <QRCodeGenerate
              pixKeys={pixKeys}
              setPixKey={setPixKey}
              setDescription={setDescription}
              amount={rawAmount}
            />
            <div className="receive__buttons">
              {renderBackButton()}
              <AdvancedButton
                text={translate('PAGES.HOME.PIX.QRCODE.GENERATE')}
                onClick={() => handleCreateQRCode()}
              />
            </div>
          </>
        ),
      },
      {
        visible: receiveType === 'qrCode' && !!qrCodeContent,
        label: translate('PAGES.HOME.RECEIVE.TABS.FOUR.QR_CODE'),
        value: TabIndexEnum.QR_CODE_DETAILS,
        children: (
          <>
            <QRCodeDetails
              description={true}
              buttons={true}
              content={qrCodeContent ?? ''}
              center
            />
          </>
        ),
      },
      {
        visible: receiveType === 'billet' && !!transaction?.billetRechargeInfo,
        value: TabIndexEnum.BILLET_CONFIRMATION,
        label: translate('PAGES.HOME.RECEIVE.TABS.THREE.BILLET'),
        children: (
          <>
            <GenerateBilletConfirmation
              billetCode={transaction?.billetRechargeInfo?.typeableLine}
              amount={rawAmount}
            />
            <div className="receive__buttons">
              <AdvancedButton
                text={translate('PAGES.HOME.RECEIVE.BUTTONS.DOWNLOAD')}
                onClick={() => handleDownload()}
              />
              <AdvancedButton
                text={translate('PAGES.HOME.RECEIVE.BUTTONS.SEND_EMAIL')}
                onClick={() => handleEmail()}
              />
              <Tooltip
                title={translate('PAGES.HOME.RECEIVE.BUTTONS.COPIED')}
                trigger="click"
                placement="rightTop"
                onOpenChange={handleCopyCode}
                mouseLeaveDelay={0.5}
                mouseEnterDelay={0.2}
              >
                <AdvancedButton
                  text={translate('PAGES.HOME.RECEIVE.BUTTONS.COPY')}
                  size="large"
                />
              </Tooltip>
            </div>
          </>
        ),
      },
    ];

    const visibleSteps = steps.filter((step) => step.visible);

    const mappedSteps = visibleSteps.map((step, index) => ({
      ...step,
      value: String(index + 1),
    }));

    const currentStepIndex = visibleSteps.findIndex((s) => s.value === tabKey);
    const mappedCurrentStep =
      currentStepIndex !== -1 ? String(currentStepIndex + 1) : '1';

    return (
      <>
        <AdvancedSteps
          testId="receive-steps"
          steps={mappedSteps}
          currentStep={mappedCurrentStep}
          onStepChange={(step) => {
            const originalStep = visibleSteps[Number(step) - 1];
            if (originalStep) {
              setTabKey(String(originalStep.value));
            }
          }}
        />
      </>
    );
  }, [
    actions,
    additionalBilletInfo,
    amount,
    bankDataOptions,
    changeAdditionalInfo,
    description,
    handleCreateQRCode,
    handleDownload,
    handleEmail,
    handleGenerateBillet,
    handleQRCodeValue,
    pixKeys,
    qrCodeContent,
    rawAmount,
    receiveType,
    renderBackButton,
    setDescription,
    setPixKey,
    tabKey,
    transaction?.billetRechargeInfo?.typeableLine,
    handleCopyCode,
    currentTheme,
    hoveredIndex,
  ]);

  return (
    <>
      <AdvancedHeader
        showBreadCrumbs
        breadCrumbs={[
          {
            active: true,
            title: translate('COMPONENTS.BREADCRUMB.HOME'),
            url: `${getRouteStackPath('HOME', 'HOME_PAGE')}`,
          },
          ...(location.state === 'pix-charge'
            ? [
                {
                  active: true,
                  title: translate('PAGES.HOME.PIX.PAGE_TITLE'),
                  url: `${getRouteStackPath('HOME', 'PIX_HOME')}`,
                },
              ]
            : []),
          {
            active: false,
            title: translate('PAGES.HOME.RECEIVE.TITLE'),
          },
        ]}
      />
      <div className="receive">
        <AdvancedPanelContentHeader
          testId="receive-header"
          title={translate('PAGES.HOME.RECEIVE.TITLE')}
          description={translate('PAGES.HOME.RECEIVE.DESCRIPTION')}
          goBackAction={() => navigate.back()}
          buttonTitle={
            location.state === 'pix-charge'
              ? translate('PAGES.HOME.PIX.BUTTONS.BACK_PIX')
              : translate('APPLICATION.BACK_TO_INIT')
          }
        />
        {!availableSoon.receivable ? (
          stepOptions
        ) : (
          <AdvancedAlert>
            {translate('PAGES.HOME.PIX.AVAILABLE_SOON')}
          </AdvancedAlert>
        )}
      </div>
      <MaintenanceModal
        setVisible={setOpenMaintenanceModal}
        visible={openMaintenanceModal}
      />
    </>
  );
};

export default Receive;
